import { useMemo, useState, useEffect } from "react";
import DataTable from "components/shared-components/data-table/DataTable";
import {
  Chip,
  ListItemIcon,
  MenuItem,
  Paper,
  Typography,
  Button,
} from "@mui/material";
import clsx from "clsx";
import { Link } from "react-router-dom";
import FuseSvgIcon from "components/shared-components/FuseSvgIcon";
import FuseLoading from "components/shared-components/FuseLoading";

// uid generator
let _uid = 0;
function generateId(prefix = "product-") {
  _uid += 1;
  return `${prefix}${_uid}`;
}

function ProductModel(data = {}) {
  return {
    id: data.id || generateId(),
    name: data.name || "Sample Product",
    handle: data.handle || "sample-product",
    description: data.description || "",
    categories: Array.isArray(data.categories) ? data.categories : [],
    tags: Array.isArray(data.tags) ? data.tags : [],
    featuredImageId: data.featuredImageId || null,
    images: Array.isArray(data.images) ? data.images : [],
    priceTaxExcl: data.priceTaxExcl ?? 0,
    priceTaxIncl: data.priceTaxIncl ?? 0,
    taxRate: data.taxRate ?? 0,
    comparedPrice: data.comparedPrice ?? 0,
    quantity: data.quantity ?? 0,
    sku: data.sku || "",
    width: data.width || "",
    height: data.height || "",
    depth: data.depth || "",
    weight: data.weight || "",
    extraShippingFee: data.extraShippingFee ?? 0,
    price: data.price || "",
    active: data.active ?? true,
    image: data.image || "",
    total: data.total || "",
  };
}

function ProductsTable() {
  const [products, setProducts] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setTimeout(() => {
      const dummy = [
        ProductModel({
          name: "Product One",
          handle: "product-one",
          categories: ["Category A", "Category B"],
          images: [
            { id: "img1", url: "https://via.placeholder.com/150" },
            { id: "img2", url: "https://via.placeholder.com/100" },
          ],
          featuredImageId: "img1",
          priceTaxIncl: 99.99,
          quantity: 10,
          active: true,
        }),
        ProductModel({
          name: "Product Two",
          handle: "product-two",
          categories: ["Category C"],
          images: [],
          featuredImageId: null,
          priceTaxIncl: 49.5,
          quantity: 3,
          active: false,
        }),
        // more dummy items
      ];
      setProducts(dummy);
      setIsLoading(false);
    }, 700);
  }, []);

  const removeProducts = (ids) => {
    setProducts((prev = []) => prev.filter((p) => !ids.includes(p.id)));
  };

  const columns = useMemo(
    () => [
      {
        accessorFn: (row) => row.featuredImageId,
        id: "featuredImageId",
        header: "",
        enableColumnFilter: false,
        enableColumnDragging: false,
        size: 64,
        enableSorting: false,
        Cell: ({ row }) => {
          const orig = row.original || {};
          const images = orig.images || [];
          const featId = orig.featuredImageId;
          const imgObj = images.find((img) => img.id === featId);
          return (
            <div className="flex items-center justify-center">
              {imgObj ? (
                <img
                  className="w-full max-h-36 max-w-36 block rounded"
                  src={imgObj.url}
                  alt={orig.name}
                />
              ) : (
                <img
                  className="w-full max-h-36 max-w-36 block rounded"
                  src="https://via.placeholder.com/100x100?text=No+Image"
                  alt={orig?.name || ""}
                />
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "name",
        header: "Name",
        Cell: ({ row }) => {
          const orig = row.original || {};
          return (
            <Typography
              component={Link}
              to={`/apps-ecommerce/products/${orig.id}/${orig.handle}`}
              className="underline"
              color="secondary"
              role="button"
            >
              {orig.name}
            </Typography>
          );
        },
      },
      {
        accessorKey: "categories",
        header: "Category",
        accessorFn: (row) => {
          const orig = row.original || {};
          return orig.categories || [];
        },
        Cell: ({ row }) => {
          const orig = row.original || {};
          const cats = orig.categories || [];
          return (
            <div className="flex flex-wrap space-x-2">
              {cats.map((item, idx) => (
                <Chip
                  key={idx}
                  className="text-sm"
                  size="small"
                  color="default"
                  label={item}
                />
              ))}
            </div>
          );
        },
      },
      {
        accessorKey: "priceTaxIncl",
        header: "Price",
        accessorFn: (row) => {
          const orig = row.original || {};
          return `$${orig.priceTaxIncl ?? 0}`;
        },
      },
      {
        accessorKey: "quantity",
        header: "Quantity",
        accessorFn: (row) => {
          const orig = row.original || {};
          const qty = orig.quantity ?? 0;
          return (
            <div className="flex items-center space-x-8">
              <span>{qty}</span>
              <i
                className={clsx(
                  "inline-block w-8 h-8 rounded",
                  qty <= 5 && "bg-red",
                  qty > 5 && qty <= 25 && "bg-orange",
                  qty > 25 && "bg-green"
                )}
              />
            </div>
          );
        },
      },
      {
        accessorKey: "active",
        header: "Active",
        accessorFn: (row) => {
          const orig = row.original || {};
          return orig.active;
        },
        Cell: ({ row }) => {
          const orig = row.original || {};
          return (
            <div className="flex items-center">
              {orig.active ? (
                <FuseSvgIcon className="text-green" size={20}>
                  heroicons-outline:check-circle
                </FuseSvgIcon>
              ) : (
                <FuseSvgIcon className="text-red" size={20}>
                  heroicons-outline:minus-circle
                </FuseSvgIcon>
              )}
            </div>
          );
        },
      },
    ],
    []
  );

  if (isLoading || !Array.isArray(products)) {
    return <FuseLoading />;
  }

  return (
    <Paper
      className="flex flex-col flex-auto shadow-1 rounded-t-lg overflow-hidden rounded-b-0 w-full h-full"
      elevation={0}
    >
      <DataTable
        data={products}
        columns={columns}
        renderRowActionMenuItems={({ closeMenu, row, table }) => [
          <MenuItem
            key={0}
            onClick={() => {
              removeProducts([row.original?.id]);
              closeMenu();
              table.resetRowSelection();
            }}
          >
            <ListItemIcon>
              <FuseSvgIcon>heroicons-outline:trash</FuseSvgIcon>
            </ListItemIcon>
            Delete
          </MenuItem>,
        ]}
        renderTopToolbarCustomActions={({ table }) => {
          const { rowSelection } = table.getState();
          if (Object.keys(rowSelection).length === 0) return null;

          return (
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                const selected = table.getSelectedRowModel().rows;
                removeProducts(selected.map((r) => r.original?.id));
                table.resetRowSelection();
              }}
              className="flex shrink min-w-36 ltr:mr-8 rtl:ml-8"
              color="secondary"
            >
              <FuseSvgIcon size={16}>heroicons-outline:trash</FuseSvgIcon>
              <span className="hidden sm:flex mx-8">Delete selected items</span>
            </Button>
          );
        }}
      />
    </Paper>
  );
}

export default ProductsTable;

// /* eslint-disable react/no-unstable-nested-components */
// import { useMemo } from "react";
// import DataTable from "components/shared-components/data-table/DataTable";
// import { Chip, ListItemIcon, MenuItem, Paper } from "@mui/material";
// import _ from "lodash";
// import { Link } from "react-router-dom";
// import Typography from "@mui/material/Typography";
// import clsx from "clsx";
// import Button from "@mui/material/Button";
// import FuseSvgIcon from "components/shared-components/FuseSvgIcon";
// import FuseLoading from "components/shared-components/FuseLoading";
// // import { useDeleteECommerceProductsMutation, useGetECommerceProductsQuery } from '../ECommerceApi';

// function ProductsTable() {
//   const { data: products, isLoading } = useGetECommerceProductsQuery();
//   const [removeProducts] = useDeleteECommerceProductsMutation();
//   const columns = useMemo(
//     () => [
//       {
//         accessorFn: (row) => row.featuredImageId,
//         id: "featuredImageId",
//         header: "",
//         enableColumnFilter: false,
//         enableColumnDragging: false,
//         size: 64,
//         enableSorting: false,
//         Cell: ({ row }) => (
//           <div className="flex items-center justify-center">
//             {row.original?.images?.length > 0 &&
//             row.original.featuredImageId ? (
//               <img
//                 className="w-full max-h-36 max-w-36 block rounded"
//                 src={
//                   _.find(row.original.images, {
//                     id: row.original.featuredImageId,
//                   })?.url
//                 }
//                 alt={row.original.name}
//               />
//             ) : (
//               <img
//                 className="w-full max-h-36 max-w-36 block rounded"
//                 src="assets/images/apps/ecommerce/product-image-placeholder.png"
//                 alt={row.original.name}
//               />
//             )}
//           </div>
//         ),
//       },
//       {
//         accessorKey: "name",
//         header: "Name",
//         Cell: ({ row }) => (
//           <Typography
//             component={Link}
//             to={`/apps/e-commerce/products/${row.original.id}/${row.original.handle}`}
//             className="underline"
//             color="secondary"
//             role="button"
//           >
//             {row.original.name}
//           </Typography>
//         ),
//       },
//       {
//         accessorKey: "categories",
//         header: "Category",
//         accessorFn: (row) => (
//           <div className="flex flex-wrap space-x-2">
//             {row.categories.map((item) => (
//               <Chip
//                 key={item}
//                 className="text-sm"
//                 size="small"
//                 color="default"
//                 label={item}
//               />
//             ))}
//           </div>
//         ),
//       },
//       {
//         accessorKey: "priceTaxIncl",
//         header: "Price",
//         accessorFn: (row) => `$${row.priceTaxIncl}`,
//       },
//       {
//         accessorKey: "quantity",
//         header: "Quantity",
//         accessorFn: (row) => (
//           <div className="flex items-center space-x-8">
//             <span>{row.quantity}</span>
//             <i
//               className={clsx(
//                 "inline-block w-8 h-8 rounded",
//                 row.quantity <= 5 && "bg-red",
//                 row.quantity > 5 && row.quantity <= 25 && "bg-orange",
//                 row.quantity > 25 && "bg-green"
//               )}
//             />
//           </div>
//         ),
//       },
//       {
//         accessorKey: "active",
//         header: "Active",
//         accessorFn: (row) => (
//           <div className="flex items-center">
//             {row.active ? (
//               <FuseSvgIcon className="text-green" size={20}>
//                 heroicons-outline:check-circle
//               </FuseSvgIcon>
//             ) : (
//               <FuseSvgIcon className="text-red" size={20}>
//                 heroicons-outline:minus-circle
//               </FuseSvgIcon>
//             )}
//           </div>
//         ),
//       },
//     ],
//     []
//   );

//   if (isLoading) {
//     return <FuseLoading />;
//   }

//   return (
//     <Paper
//       className="flex flex-col flex-auto shadow-1 rounded-t-lg overflow-hidden rounded-b-0 w-full h-full"
//       elevation={0}
//     >
//       <DataTable
//         data={products}
//         columns={columns}
//         renderRowActionMenuItems={({ closeMenu, row, table }) => [
//           <MenuItem
//             key={0}
//             onClick={() => {
//               removeProducts([row.original.id]);
//               closeMenu();
//               table.resetRowSelection();
//             }}
//           >
//             <ListItemIcon>
//               <FuseSvgIcon>heroicons-outline:trash</FuseSvgIcon>
//             </ListItemIcon>
//             Delete
//           </MenuItem>,
//         ]}
//         renderTopToolbarCustomActions={({ table }) => {
//           const { rowSelection } = table.getState();

//           if (Object.keys(rowSelection).length === 0) {
//             return null;
//           }

//           return (
//             <Button
//               variant="contained"
//               size="small"
//               onClick={() => {
//                 const selectedRows = table.getSelectedRowModel().rows;
//                 removeProducts(selectedRows.map((row) => row.original.id));
//                 table.resetRowSelection();
//               }}
//               className="flex shrink min-w-36 ltr:mr-8 rtl:ml-8"
//               color="secondary"
//             >
//               <FuseSvgIcon size={16}>heroicons-outline:trash</FuseSvgIcon>
//               <span className="hidden sm:flex mx-8">Delete selected items</span>
//             </Button>
//           );
//         }}
//       />
//     </Paper>
//   );
// }

// export default ProductsTable;
